import React, { useState } from 'react'
import { Grid, useMediaQuery, Tabs, Tab, Theme } from '@material-ui/core'
import useIntl from 'hooks/useIntl'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import ChallengeProgress, {
	ChallengeProgressProps
} from './components/ChallengeProgress/ChallengeProgress'
import ViewSubmissionModal from 'components/ViewSubmissionModal'
import { CoreChapterResultFieldsFragment } from 'hooks/useApollo'
import ProfileHeader, { ProfileHeaderProps } from './components/ProfileHeader'
import ProfileTab, { ProfileTabProps } from './tabs/ProfileTab'
import ChallengeTab, { ChallengeTabProps } from './tabs/ChallengeTab'
import NotesTab, { NotesTabProps } from './tabs/NotesTab'
import { makeStyles } from '@material-ui/core/styles'
const useStyles = makeStyles(theme => ({
	gridItem: {
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary
	},
	tabRoot: {
		margin: 0,
		padding: 0,
		minHeight: 'unset',
		backgroundColor: theme.palette.background.paper,
		color: theme.palette.text.primary
	},
	tab: {
		minWidth: 'auto',
		padding: '6px 12px',
		color: 'inherit'
	},
	pdfExportLightMode: {
		'& #right-side-content': {
			backgroundColor: 'transparent  !important',
			color: 'black !important'
		},
		'& #right-side-content *': {
			backgroundColor: 'transparent !important',
			color: 'black !important'
		}
	}
}))
export interface TalentProfileProps {
	profileHeaderProps: ProfileHeaderProps
	challengeProgressProps: ChallengeProgressProps
	profileTabProps: ProfileTabProps
	challengeTabProps: ChallengeTabProps
	notesTabProps: NotesTabProps
	demo?: boolean
}

const TalentProfile: React.FC<
	TalentProfileProps & { submissionData: CoreChapterResultFieldsFragment }
> = ({
	challengeProgressProps,
	profileHeaderProps,
	profileTabProps,
	challengeTabProps,
	notesTabProps,
	demo,
	submissionData
}) => {
	const classes = useStyles()
	const intl = useIntl()
	const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
	const [activeTab, setActiveTab] = useState<number>(0)
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
	const [exportInProgress, setExportInProgress] = useState<boolean>(false)
	const exportToPDF = async () => {
		const element = document.getElementById('right-side-content')
		if (!element) return
		const originalTheme = element.getAttribute('data-theme') || ''
		const isDark = document.body.classList.contains('dark')

		element.setAttribute('data-theme', 'light')
		document.body.classList.add(classes.pdfExportLightMode)
		if (isDark) document.body.classList.remove('dark')

		setExportInProgress(true)

		await new Promise(resolve => setTimeout(resolve, 0))

		const canvas = await html2canvas(element, {
			scale: 2,
			useCORS: true,
			logging: false,
			allowTaint: true
		})
		element.setAttribute('data-theme', originalTheme)
		document.body.classList.remove(classes.pdfExportLightMode)
		if (isDark) document.body.classList.add('dark')

		const imgData = canvas.toDataURL('image/png')
		const pdf = new jsPDF('p', 'mm', 'a4')
		const pageWidth = pdf.internal.pageSize.getWidth()
		const pageHeight = pdf.internal.pageSize.getHeight()
		const margin = 9
		const imgWidth = pageWidth - margin * 2
		const imgHeight = (canvas.height * imgWidth) / canvas.width

		const { firstName, lastName } = profileHeaderProps

		const paddingX = 5
		const paddingY = 6
		const cardGap = 6
		const blockHeight = 5
		const borderRadius = 3

		const yellowRGB: [number, number, number] = [255, 243, 205]
		const greyRGB: [number, number, number] = [240, 240, 240]
		const green = '#008000'
		const red = '#FF0000'

		pdf.setFontSize(16)
		pdf.text(`${firstName} ${lastName} Profile`, margin, margin + 5)
		pdf.addImage(
			imgData,
			'PNG',
			margin,
			margin + 10,
			imgWidth,
			imgHeight,
			undefined,
			'FAST'
		)

		let yPosition = margin + 10 + imgHeight + 10
		const minimumSpaceNeeded = 60
		if (yPosition + minimumSpaceNeeded > pageHeight - margin) {
			pdf.addPage()
			yPosition = margin
		}

		pdf.setFontSize(14)
		pdf.setFont('helvetica', 'bold')
		pdf.text('Submission Details:', margin, yPosition)
		yPosition += 10
		pdf.setFontSize(12)

		submissionData?.parts?.forEach(
			({ scene, title, question, instruction, answer, type, options, correctAnswer }) => {
				const sectionGap = 6

				const sectionTitle = `${scene || ''} ${title || ''}`.trim()
				const sectionLines = pdf.splitTextToSize(sectionTitle, pageWidth - margin * 2)
				pdf.setFont('helvetica', 'bold')
				sectionLines.forEach(line => {
					if (yPosition + blockHeight > pageHeight - margin) {
						pdf.addPage()
						yPosition = margin
					}
					pdf.text(line, margin, yPosition)
					yPosition += blockHeight
				})

				const questionText = `${!correctAnswer ? question || '' : ''}\n${
					instruction || ''
				}`.trim()
				const questionLines = pdf.splitTextToSize(
					questionText,
					pageWidth - margin * 2 - paddingX * 2
				)
				const cardHeight = questionLines.length * blockHeight + paddingY * 3

				if (yPosition + cardHeight > pageHeight - margin) {
					pdf.addPage()
					yPosition = margin
				}

				pdf.setFillColor(...yellowRGB)
				pdf.setDrawColor(200, 200, 200)
				pdf.roundedRect(
					margin,
					yPosition,
					pageWidth - margin * 2,
					cardHeight,
					borderRadius,
					borderRadius,
					'FD'
				)

				let cardTextY = yPosition + paddingY
				pdf.setFont('helvetica', 'normal')
				questionLines.forEach(line => {
					pdf.text(line, margin + paddingX, cardTextY)
					cardTextY += blockHeight
				})
				yPosition += cardHeight + cardGap

				pdf.setFont('helvetica', 'bold')
				pdf.text('Answer:', margin, yPosition)
				yPosition += blockHeight
				pdf.setFont('helvetica', 'normal')

				if (type === 'options' && options) {
					options.forEach(opt => {
						const isCorrect = Array.isArray(correctAnswer)
							? correctAnswer.includes(opt)
							: correctAnswer === opt
						const isUserAnswer = opt === answer
						const isMatched = isUserAnswer && isCorrect
						const isIncorrect = isUserAnswer && !isCorrect

						const optPrefix = isMatched ? '✔ ' : isIncorrect ? '✖ ' : '* '
						const optColor = isMatched ? green : isIncorrect ? red : '#000'

						const optText = `${optPrefix}${opt}`
						const optLines = pdf.splitTextToSize(
							optText,
							pageWidth - margin * 2 - paddingX * 2
						)
						const optHeight = optLines.length * blockHeight + paddingY * 2

						if (yPosition + optHeight > pageHeight - margin) {
							pdf.addPage()
							yPosition = margin
						}

						pdf.setFillColor(...greyRGB)
						pdf.setDrawColor(220, 220, 220)
						pdf.roundedRect(
							margin,
							yPosition,
							pageWidth - margin * 2,
							optHeight,
							borderRadius,
							borderRadius,
							'FD'
						)

						let optTextY = yPosition + paddingY
						pdf.setTextColor(optColor)
						optLines.forEach(line => {
							pdf.text(line, margin + paddingX, optTextY)
							optTextY += blockHeight
						})
						pdf.setTextColor('#000')
						yPosition += optHeight + cardGap
					})
				} else if (type === 'answer') {
					const ansText = answer || 'N/A'
					const ansLines = pdf.splitTextToSize(
						ansText,
						pageWidth - margin * 2 - paddingX * 2
					)
					const ansBlockHeight = ansLines.length * blockHeight + paddingY * 2

					if (yPosition + ansBlockHeight > pageHeight - margin) {
						pdf.addPage()
						yPosition = margin
					}

					pdf.setFillColor(...greyRGB)
					pdf.setDrawColor(220, 220, 220)
					pdf.roundedRect(
						margin,
						yPosition,
						pageWidth - margin * 2,
						ansBlockHeight,
						borderRadius,
						borderRadius,
						'FD'
					)

					let ansTextY = yPosition + paddingY
					ansLines.forEach(line => {
						pdf.text(line, margin + paddingX, ansTextY)
						ansTextY += blockHeight
					})

					yPosition += ansBlockHeight + cardGap
				}

				if (correctAnswer) {
					const correctLabel = 'Correct Answer:'
					const correctText = Array.isArray(correctAnswer)
						? correctAnswer.join(', ')
						: correctAnswer

					const labelHeight = blockHeight
					const correctLines = pdf.splitTextToSize(
						correctText,
						pageWidth - margin * 2 - paddingX * 2
					)

					const verticalPadding = 4
					const lineHeight = blockHeight - 2
					const correctBlockHeight = correctLines.length * lineHeight + verticalPadding * 2

					if (yPosition + labelHeight + correctBlockHeight > pageHeight - margin) {
						pdf.addPage()
						yPosition = margin
					}

					pdf.setFont('helvetica', 'bold')
					pdf.text(correctLabel, margin, yPosition)
					yPosition += labelHeight

					pdf.setFont('helvetica', 'normal')
					const maxLineWidth = Math.max(...correctLines.map(line => pdf.getTextWidth(line)))
					const rectX = margin + paddingX
					const rectY = yPosition
					const rectWidth = maxLineWidth + paddingX * 2
					const rectHeight = correctBlockHeight

					pdf.setFillColor(200, 255, 200)
					pdf.setDrawColor(180, 220, 180)
					pdf.roundedRect(
						rectX,
						rectY,
						rectWidth,
						rectHeight,
						borderRadius,
						borderRadius,
						'FD'
					)

					let correctTextY =
						rectY + (rectHeight - correctLines.length * lineHeight) / 2 + lineHeight * 0.75

					correctLines.forEach(line => {
						const textWidth = pdf.getTextWidth(line)
						const textX = rectX + (rectWidth - textWidth) / 2
						pdf.text(line, textX, correctTextY)
						correctTextY += lineHeight
					})

					yPosition += correctBlockHeight
				}

				yPosition += sectionGap
			}
		)

		pdf.save(`${firstName} ${lastName} Profile.pdf`)
		setExportInProgress(false)
	}

	return (
		<Grid container spacing={0} id="right-side-content" className={classes.gridItem}>
			<Grid item xs={12}>
				<ProfileHeader {...profileHeaderProps} exportToPDF={exportToPDF} />
			</Grid>
			<Grid item xs={12} style={{ marginTop: 3 }}>
				<ChallengeProgress demo={demo} {...challengeProgressProps} />
			</Grid>
			<Grid item xs={12}>
				<Tabs
					value={activeTab}
					onChange={(_, newValue) => setActiveTab(newValue)}
					variant={mdDown ? 'fullWidth' : 'scrollable'}
					classes={{ root: classes.tabRoot }}
				>
					<Tab label="Profile" className={classes.tab} />
					<Tab label="Challenge Submission" className={classes.tab} />
					<Tab label="Notes" className={classes.tab} />
				</Tabs>
			</Grid>
			<Grid item xs={12} className={classes.gridItem}>
				<div data-tab-content style={{ display: activeTab === 0 ? 'block' : 'none' }}>
					<ProfileTab {...profileTabProps} />
				</div>
				<div data-tab-content style={{ display: activeTab === 1 ? 'block' : 'none' }}>
					<ChallengeTab {...challengeTabProps} />
					{(isModalVisible || exportInProgress) && (
						<div id="modal-wrapper">
							<ViewSubmissionModal
								modalProps={{ open: false, onClose: () => {} }}
								title="Submission Details"
								desc="Challenge Submission"
								data={submissionData}
								onNext={() => {}}
								onPrev={() => {}}
								isFirstIndex={false}
								isLastIndex={false}
								className="submission-modal-content"
							/>
						</div>
					)}
				</div>
				<div data-tab-content style={{ display: activeTab === 2 ? 'block' : 'none' }}>
					<NotesTab {...notesTabProps} />
				</div>
			</Grid>
		</Grid>
	)
}

export default TalentProfile
